import React, { useMemo, useState } from "react";
import dayjs from "dayjs";
import BasicModal from "./basicmodal";
import VitalsModal from "./vitalsmodal";
import { OtherModal } from "./othermodal";
import api from "../../../../api/api";
import ToastMessage from "../../../../utils/mui/righttoptoast";
import { Backdrop, CircularProgress } from "@mui/material";
import PreviousReadings from "./previousreadings";

const actionBtn =
  "h-10 rounded-md bg-[#1D2951] px-6 text-sm font-semibold text-white";

export default function Intake({
  data = {},
  pharmadetails,
  pain,
  previousdata = {},
  setFetchedData,
  fetchpreviousvitals,
}) {
  const practiseid = window.getPracticeId();
  const [tab, setTab] = useState("basic");

  // Toast + loader
  const [isLoading, setIsLoading] = useState(false);
  const [openToast, setOpenToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [editTarget, setEditTarget] = useState(null);
  const [toastSeverity, setToastSeverity] = useState("success");
  const handleToastClose = () => setOpenToast(false);
  const [renderKey, setRenderKey] = useState(0);
  // --- at the top of Intake.jsx (inside the component file, above the component) ---
  const defaultF = {
    bpSys: "",
    bpDia: "",
    bpPos: "L Arm",
    pulse: "",
    pulseType: "regular",
    weight: "",
    weightUnit: "lbs",
    heightFt: "",
    heightIn: "",
    temp: "",
    tempUnit: "°F",
    tempSite: "Ear",
    o2: "",
    o2Type: "Standing",
    rr: "",
    rrType: "Standing",
    glucose: "",
    glucoseUnit: "mg/dl",
    glucoseFasting: "Not-fasting",
    vodR: "",
    vodL: "",
    vosR: "",
    vosL: "",
    vosUnit: "cm",
    waist: "",
    birthWeight: "",
  };

  // Unified parent state (everything that will be sent to API)
  const [f, setF] = useState(defaultF);
  const update = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const height = useMemo(() => {
    const ft = parseFloat(f.heightFt || "0");
    const inches = parseFloat(f.heightIn || "0");
    const totalInches = ft * 12 + inches;
    return {
      inches: totalInches || 0,
      meters: totalInches ? totalInches * 0.0254 : 0,
      centimeters: totalInches ? +(totalInches * 2.54).toFixed(2) : 0,
    };
  }, [f.heightFt, f.heightIn]);

  const bmi = useMemo(() => {
    const w = parseFloat(f.weight || "0");
    if (!w || !height.meters) return "";
    const kg =
      f.weightUnit === "lbs"
        ? w * 0.45359237
        : f.weightUnit === "stone"
          ? w * 6.35029
          : w;
    return (kg / (height.meters * height.meters)).toFixed(2);
  }, [f.weight, f.weightUnit, height.meters]);

  const getCurrentDate = () => dayjs().format("MM/DD/YYYY");

  // ---- NEW: tab helpers for Next button ----
  const TAB_ORDER = ["basic", "vitals", "other"];
  const goNext = () => {
    setTab((t) => {
      const idx = TAB_ORDER.indexOf(t);
      return TAB_ORDER[Math.min(idx + 1, TAB_ORDER.length - 1)];
    });
  };

  async function handleSave() {
    try {
      setIsLoading(true);
      let trimmedvitalsdata;
      let trimmedvitalsvalues = {};
      if (tab === "basic") {
        // Weight → grams
        const wNum = parseFloat(f.weight || "0");
        let weightInGrams = 0;
        if (f.weightUnit === "kg") weightInGrams = wNum * 1000;
        else if (f.weightUnit === "lbs") weightInGrams = wNum * 453.59237;
        else if (f.weightUnit === "stone") weightInGrams = wNum * 6350.29;
        trimmedvitalsdata = [
          // HEIGHT in cm
          f.heightFt || f.heightIn
            ? [
                {
                  clinicalelementid: "VITALS.HEIGHT",
                  readingtaken: getCurrentDate(),
                  value: height.centimeters,
                },
              ]
            : [],

          // WEIGHT + TYPE
          f.weight
            ? [
                {
                  clinicalelementid: "VITALS.WEIGHT",
                  readingtaken: getCurrentDate(),
                  value: weightInGrams.toFixed(2),
                },
                {
                  clinicalelementid: "VITALS.WEIGHT.TYPE",
                  readingtaken: getCurrentDate(),
                  value: "With clothes",
                },
              ]
            : [],

          // BMI
          bmi
            ? [
                {
                  clinicalelementid: "VITALS.BMI",
                  readingtaken: getCurrentDate(),
                  value: bmi,
                },
              ]
            : [],

          // BLOOD PRESSURE
          f.bpSys || f.bpDia
            ? [
                f.bpSys && {
                  clinicalelementid: "VITALS.BLOODPRESSURE.SYSTOLIC",
                  readingtaken: getCurrentDate(),
                  value: f.bpSys,
                },
                f.bpDia && {
                  clinicalelementid: "VITALS.BLOODPRESSURE.DIASTOLIC",
                  readingtaken: getCurrentDate(),
                  value: f.bpDia,
                },
              ].filter(Boolean)
            : [],

          // PULSE
          f.pulse
            ? [
                {
                  clinicalelementid: "VITALS.PULSE.RATE",
                  readingtaken: getCurrentDate(),
                  value: f.pulse,
                },
                {
                  clinicalelementid: "VITALS.PULSE.TYPE",
                  readingtaken: getCurrentDate(),
                  value: f.pulseType,
                },
              ]
            : [],
        ].filter((arr) => arr.length > 0);
        trimmedvitalsvalues = {
          bmi: bmi,
          pain_level: pain || "0",
          height_ft: f.heightFt,
          height_in: f.heightIn,
          height_pos: "standing",
          bp_mm: f.bpSys,
          bp_hgg: f.bpDia,
          bp_pos: f.bpPos,
          bp_drop: "",

          bp1_mm: previousdata?.bp_mm || "",
          bp1_hgg: previousdata?.bp_hgg || "",
          bp1_pos: previousdata?.bp_pos || "",
          bp1_drop: "",

          bp2_mm: previousdata?.bp1_mm || "",
          bp2_hgg: previousdata?.bp1_hgg || "",
          bp2_pos: previousdata?.bp1_pos || "",
          bp2_drop: "",

          bp3_mm: previousdata?.bp2_mm || "",
          bp3_hgg: previousdata?.bp2_hgg || "",
          bp3_pos: previousdata?.bp2_pos || "",
          bp3_drop: "",

          plus_bpm: f.pulse,
          plus_drop: f.pulseType,
          weight: f.weight,
          weight_cel: f.weightUnit,
          weight_drop: "With clothes",
        };
      }
      if (tab === "vitals") {
        trimmedvitalsdata = [
          // TEMPERATURE (value only)
          f.temp
            ? [
                {
                  clinicalelementid: "VITALS.TEMPERATURE",
                  readingtaken: getCurrentDate(),
                  value: f.temp,
                },
                // Site/unit can be added later if needed
              ]
            : [],

          // O2 Saturation
          f.o2
            ? [
                {
                  clinicalelementid: "VITALS.O2SATURATION",
                  readingtaken: getCurrentDate(),
                  value: f.o2,
                },
              ]
            : [],

          // Respiratory
          f.rr
            ? [
                {
                  clinicalelementid: "VITALS.RESPIRATIONRATE",
                  readingtaken: getCurrentDate(),
                  value: f.rr,
                },
              ]
            : [],
        ].filter((arr) => arr.length > 0);

        trimmedvitalsvalues = {
          pain_level: pain || "0",

          temp: f.temp,
          temp_cel: f.tempUnit,
          temp_drop: f.tempSite,

          o2_1: f.o2,
          o2_2: "",
          o2_drop: f.o2Type,
          respiratory: f.rr,
          respiratory_drop: f.rrType,
        };
      }
      if (tab === "other") {
        trimmedvitalsdata = [
          // O2 Saturation
          f.o2
            ? [
                {
                  clinicalelementid: "VITALS.O2SATURATION",
                  readingtaken: getCurrentDate(),
                  value: f.o2,
                },
              ]
            : [],

          // Respiratory
          f.rr
            ? [
                {
                  clinicalelementid: "VITALS.RESPIRATIONRATE",
                  readingtaken: getCurrentDate(),
                  value: f.rr,
                },
              ]
            : [],

          // Vision (combined R/L as per current approach)
          f.vodR || f.vodL
            ? [
                {
                  clinicalelementid: "VITALS.VOD",
                  readingtaken: getCurrentDate(),
                  value: `${f.vodR || 20}/${f.vodL || 20}`,
                },
              ]
            : [],
          f.vosR || f.vosL
            ? [
                {
                  clinicalelementid: "VITALS.VOS",
                  readingtaken: getCurrentDate(),
                  value: `${f.vosR || 20}/${f.vosL || 20}`,
                },
              ]
            : [],

          // // Notes (CBG)
          [
            {
              clinicalelementid: "VITALS.NOTES",
              readingtaken: getCurrentDate(),
              value: `CBG: ${f.glucose || ""} ${f.glucoseUnit || ""} ${
                f.glucoseFasting || ""
              }`,
            },
          ],
        ].filter((arr) => arr.length > 0);
        trimmedvitalsvalues = {
          pain_level: pain || "0",

          bg: f.glucose,
          bg_cel: f.glucoseUnit,
          bg_drop: f.glucoseFasting,

          right_eye: f.vodR,
          left_eye: f.vodL,
        };
      }
      // const vitalsdata = [
      //   // HEIGHT in cm
      //   f.heightFt || f.heightIn
      //     ? [
      //         {
      //           clinicalelementid: "VITALS.HEIGHT",
      //           readingtaken: getCurrentDate(),
      //           value: height.centimeters,
      //         },
      //       ]
      //     : [],

      //   // WEIGHT + TYPE
      //   f.weight
      //     ? [
      //         {
      //           clinicalelementid: "VITALS.WEIGHT",
      //           readingtaken: getCurrentDate(),
      //           value: weightInGrams.toFixed(2),
      //         },
      //         {
      //           clinicalelementid: "VITALS.WEIGHT.TYPE",
      //           readingtaken: getCurrentDate(),
      //           value: "With clothes",
      //         },
      //       ]
      //     : [],

      //   // BMI
      //   bmi
      //     ? [
      //         {
      //           clinicalelementid: "VITALS.BMI",
      //           readingtaken: getCurrentDate(),
      //           value: bmi,
      //         },
      //       ]
      //     : [],

      //   // BLOOD PRESSURE
      //   f.bpSys || f.bpDia
      //     ? [
      //         f.bpSys && {
      //           clinicalelementid: "VITALS.BLOODPRESSURE.SYSTOLIC",
      //           readingtaken: getCurrentDate(),
      //           value: f.bpSys,
      //         },
      //         f.bpDia && {
      //           clinicalelementid: "VITALS.BLOODPRESSURE.DIASTOLIC",
      //           readingtaken: getCurrentDate(),
      //           value: f.bpDia,
      //         },
      //       ].filter(Boolean)
      //     : [],

      //   // TEMPERATURE (value only)
      //   f.temp
      //     ? [
      //         {
      //           clinicalelementid: "VITALS.TEMPERATURE",
      //           readingtaken: getCurrentDate(),
      //           value: f.temp,
      //         },
      //         // Site/unit can be added later if needed
      //       ]
      //     : [],

      //   // PULSE
      //   f.pulse
      //     ? [
      //         {
      //           clinicalelementid: "VITALS.PULSE.RATE",
      //           readingtaken: getCurrentDate(),
      //           value: f.pulse,
      //         },
      //         {
      //           clinicalelementid: "VITALS.PULSE.TYPE",
      //           readingtaken: getCurrentDate(),
      //           value: f.pulseType,
      //         },
      //       ]
      //     : [],

      //   // O2 Saturation
      //   f.o2
      //     ? [
      //         {
      //           clinicalelementid: "VITALS.O2SATURATION",
      //           readingtaken: getCurrentDate(),
      //           value: f.o2,
      //         },
      //       ]
      //     : [],

      //   // Respiratory
      //   f.rr
      //     ? [
      //         {
      //           clinicalelementid: "VITALS.RESPIRATIONRATE",
      //           readingtaken: getCurrentDate(),
      //           value: f.rr,
      //         },
      //       ]
      //     : [],

      //   // Vision (combined R/L as per current approach)
      //   f.vodR || f.vodL
      //     ? [
      //         {
      //           clinicalelementid: "VITALS.VOD",
      //           readingtaken: getCurrentDate(),
      //           value: `${f.vodR || 20}/${f.vodL || 20}`,
      //         },
      //       ]
      //     : [],
      //   f.vosR || f.vosL
      //     ? [
      //         {
      //           clinicalelementid: "VITALS.VOS",
      //           readingtaken: getCurrentDate(),
      //           value: `${f.vosR || 20}/${f.vosL || 20}`,
      //         },
      //       ]
      //     : [],

      //   // Waist
      //   f.waist
      //     ? [
      //         {
      //           clinicalelementid: "VITALS.WAISTCIRCUMFERENCE",
      //           readingtaken: getCurrentDate(),
      //           value: f.waist,
      //         },
      //       ]
      //     : [],

      //   // Birth Weight
      //   f.birthWeight
      //     ? [
      //         {
      //           clinicalelementid: "VITALS.BIRTHWEIGHT",
      //           readingtaken: getCurrentDate(),
      //           value: f.birthWeight,
      //         },
      //       ]
      //     : [],

      //   // Notes (CBG)
      //   [
      //     {
      //       clinicalelementid: "VITALS.NOTES",
      //       readingtaken: getCurrentDate(),
      //       value: `CBG: ${f.glucose || ""} ${f.glucoseUnit || ""} ${
      //         f.glucoseFasting || ""
      //       }`,
      //     },
      //   ],
      // ].filter((arr) => arr.length > 0)

      // const vitalsvalus = {
      //   pain_level: "0",
      //   height_ft: f.heightFt,
      //   height_in: f.heightIn,
      //   height_pos: "standing",
      //   bp_mm: f.bpSys,
      //   bp_hgg: f.bpDia,
      //   bp_pos: f.bpPos,
      //   bp_drop: "",
      //   bp1_mm: "",
      //   bp1_hgg: "",
      //   bp1_pos: "",
      //   bp1_drop: "",
      //   bp2_mm: "",
      //   bp2_hgg: "",
      //   bp2_pos: "",
      //   bp2_drop: "",
      //   bp3_mm: "",
      //   bp3_hgg: "",
      //   bp3_pos: "",
      //   bp3_drop: "",
      //   plus_bpm: f.pulse,
      //   plus_drop: f.pulseType,
      //   weight: f.weight,
      //   weight_cel: f.weightUnit,
      //   weight_drop: "With clothes",
      //   temp: f.temp,
      //   temp_cel: f.tempUnit,
      //   temp_drop: f.tempSite,
      //   bg: f.glucose,
      //   bg_cel: f.glucoseUnit,
      //   bg_drop: f.glucoseFasting,
      //   o2_1: f.o2,
      //   o2_2: "",
      //   o2_drop: f.o2Type,
      //   respiratory: f.rr,
      //   respiratory_drop: f.rrType,
      //   right_eye: f.vodR,
      //   left_eye: f.vodL,
      //   head: "",
      //   waist: f.waist,
      //   birht: f.birthWeight,
      // }

      const payload = {
        appointment_id: data?.appointmentid,
        vitalsdata: trimmedvitalsdata,
        vitalsvalus: trimmedvitalsvalues,
      };
      let res;
      if (practiseid == 1111) {
        console.log(previousdata, "dataa");
        if (
          previousdata?.vitals_id &&
          dayjs(previousdata?.created_at).isValid() &&
          dayjs(previousdata.created_at).isSame(dayjs(), "day")
        ) {
          payload.row_id = previousdata.vitals_id;
        }
        res = await api.post(
          `/v1/${practiseid}/intake/patient/${data?.patientid}/vitals/save-local`,
          payload,
        );
      } else {
        res = await api.post(
          `/v1/${practiseid}/intake/patient/${data?.patientid}/vitals/save`,
          payload,
        );
      }

      console.log("res?.status", res);
      if (res?.status === 200) {
        if (
          !previousdata?.created_at ||
          !dayjs(previousdata.created_at).isSame(dayjs(), "day")
        ) {
          setFetchedData((prev) =>
            prev.map((obj) =>
              obj.patientid === data?.patientid
                ? { ...obj, visit_confirmation: "Ready for Provider" }
                : obj,
            ),
          );
        }

        setToastMessage("Vitals Submitted Successfully");
        setToastSeverity("success");
        setOpenToast(true);
        setF(defaultF);
        // setTab("basic");
        fetchpreviousvitals();
      } else {
        setToastMessage(res?.message || "Something went wrong");
        setToastSeverity("info");
        setOpenToast(true);
      }
    } catch (err) {
      console.error("API Error:", err);
      setToastMessage("Something went wrong");
      setToastSeverity("info");
      setOpenToast(true);
    } finally {
      setIsLoading(false);
      setEditTarget(null);
      setRenderKey((k) => k + 1);
      if (tab !== "other") {
        //  goNext()
      }
    }
  }

  return (
    <div className="w-full bg-white rounded-xl border border-[#E5E7EB]">
      {/* Secondary tabs */}
      <div className="flex gap-6  justify-between border-b border-gray-200 px-6 mt-3">
        <div className="flex gap-6">
          {[
            ["basic", "Basic Measurements"],
            ["vitals", "Vital Signs"],
            ["other", "Other"],
            ["prev", "Vitals History"],
          ].map(([key, label]) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              className={`-mb-px border-b-2 pb-3 text-sm transition focus:outline-none ${
                tab === key
                  ? "border-[#f28b6b] font-semibold text-[#f28b6b]"
                  : "border-transparent text-gray-600 hover:text-gray-800"
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        <div className="bg-[#F8FAFC] border border-[#E5E7EB] rounded-lg px-3 py-2 min-w-[200px] w-fit">
          {pharmadetails?.clinicalprovidername && (
            <div className="flex items-center gap-2">
              {/* Icon */}
              <div className="w-6 h-6 rounded-md bg-white border flex items-center justify-center text-[12px]">
                💊
              </div>

              {/* Text */}
              <div className="flex text-start flex-col leading-tight">
                <span className="text-[13px] font-medium text-[#111827]">
                  {pharmadetails.clinicalprovidername}
                </span>

                {pharmadetails?.address1 && (
                  <span className="text-[11px] text-[#6B7280] truncate max-w-[140px]">
                    {pharmadetails.address1}
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
      {/* <div key={renderKey}> */}
      <div>
        {/* <div className="flex justify-end px-4 py-1 mt-1 items-center">
   
          <button
            onClick={handleSave}
            className="rounded-md bg-[#1D2951] px-6  py-1 text-sm font-semibold text-white"
            disabled={isLoading}
          >
            Save
          </button>
     
      </div> */}
        {tab === "basic" && (
          <BasicModal
            systolic={f.bpSys}
            setSystolic={(v) => update("bpSys", v)}
            diastolic={f.bpDia}
            setDiastolic={(v) => update("bpDia", v)}
            arm={f.bpPos}
            finalsave={handleSave}
            finalload={isLoading}
            editTarget={editTarget}
            setEditTarget={setEditTarget}
            setArm={(v) => update("bpPos", v)}
            pulse={f.pulse}
            setPulse={(v) => update("pulse", v)}
            pulseType={f.pulseType}
            setPulseType={(v) => update("pulseType", v)}
            weight={f.weight}
            setWeight={(v) => update("weight", v)}
            weightUnit={f.weightUnit}
            setWeightUnit={(v) => update("weightUnit", v)}
            heightFt={f.heightFt}
            setHeightFt={(v) => update("heightFt", v)}
            heightIn={f.heightIn}
            setHeightIn={(v) => update("heightIn", v)}
            bmi={bmi}
            handleSave={handleSave}
            previousdata={previousdata}
          />
        )}

        {tab === "vitals" && (
          <VitalsModal
            temp={f.temp}
            setTemp={(v) => update("temp", v)}
            tempUnit={f.tempUnit}
            finalsave={handleSave}
            finalload={isLoading}
            setTempUnit={(v) => update("tempUnit", v)}
            tempSite={f.tempSite}
            setTempSite={(v) => update("tempSite", v)}
            pulse={f.pulse}
            setPulse={(v) => update("pulse", v)}
            pulseType={f.pulseType}
            setPulseType={(v) => update("pulseType", v)}
            o2={f.o2}
            setO2={(v) => update("o2", v)}
            o2Position={f.o2Type}
            setO2Position={(v) => update("o2Type", v)}
            resp={f.rr}
            setResp={(v) => update("rr", v)}
            respPosition={f.rrType}
            setRespPosition={(v) => update("rrType", v)}
            previousdata={previousdata}
            handleSave={handleSave}
          />
        )}

        {tab === "other" && (
          <OtherModal
            glucose={f.glucose}
            setGlucose={(v) => update("glucose", v)}
            glucoseUnit={f.glucoseUnit}
            setGlucoseUnit={(v) => update("glucoseUnit", v)}
            glucoseFasting={f.glucoseFasting}
            setGlucoseFasting={(v) => update("glucoseFasting", v)}
            vodR={f.vodR}
            finalsave={handleSave}
            finalload={isLoading}
            setVodR={(v) => update("vodR", v)}
            vodL={f.vodL}
            setVodL={(v) => update("vodL", v)}
            vosR={f.vosR}
            setVosR={(v) => update("vosR", v)}
            vosL={f.vosL}
            setVosL={(v) => update("vosL", v)}
            vosUnit={f.vosUnit}
            setVosUnit={(v) => update("vosUnit", v)}
            waist={f.waist}
            setWaist={(v) => update("waist", v)}
            birthWeight={f.birthWeight}
            setBirthWeight={(v) => update("birthWeight", v)}
            previousdata={previousdata}
            handleSave={handleSave}
          />
        )}
        {tab == "prev" && <PreviousReadings data={data} />}
      </div>
      {/* Controlled modals */}

      {/* Footer: Next (basic/vitals) -> Save (other) */}

      {/* Toast */}
      <ToastMessage
        message={toastMessage}
        severity={toastSeverity}
        open={openToast}
        handleClose={handleToastClose}
      />

      {/* Fullscreen loader */}
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 9999 }}
        open={isLoading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </div>
  );
}
